import { useEffect, useRef, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Building2, X } from "lucide-react";
import Logo from "@/components/common/Logo";
import { cn } from "@/lib/cn";
import { useAuth } from "@/context/AuthContext";
import { navForRole, ROLE_LABEL, HOME_BY_ROLE, SETTINGS_PATH } from "@/config/nav";

// `scope` namespaces the shared-layout ids. The desktop sidebar and the mobile
// drawer are BOTH mounted at once (the desktop one is only display:none below
// `lg`), so an un-namespaced layoutId gives framer-motion two live claimants for
// the same shared element. On a navigation the active item moves in both trees
// while the drawer is mid-exit, and framer never resolves the exit — leaving the
// drawer scrim mounted at opacity 0 with pointer-events:auto, an invisible
// full-screen sheet that swallows every subsequent tap until a page reload.
function NavItem({ item, onNavigate, scope }) {
  // Paused tab (e.g. Projects while cloud/headless testing is disabled): render
  // a non-clickable, greyed row with a small red "temporarily disabled" note.
  if (item.disabled) {
    return (
      <div
        aria-disabled="true"
        title={item.disabledNote || "Temporarily disabled"}
        className="relative flex cursor-not-allowed items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-400 opacity-70 dark:text-slate-500"
      >
        <span className="relative z-10 grid h-7 w-7 shrink-0 place-items-center rounded-lg text-slate-300 dark:text-slate-600">
          <item.icon size={17} />
        </span>
        <span className="relative z-10 truncate">{item.label}</span>
        <span className="relative z-10 ml-auto shrink-0 text-[0.625rem] font-semibold lowercase text-rose-500 dark:text-rose-400">
          {item.disabledNote || "temporarily disabled"}
        </span>
      </div>
    );
  }
  return (
    <NavLink to={item.to} end={item.end} onClick={onNavigate} className="group relative block">
      {({ isActive }) => (
        <ActiveNavRow isActive={isActive}>
          {isActive && (
            <motion.span
              layoutId={`${scope}-side-active`}
              className="absolute inset-0 -z-0 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 shadow-[0_10px_28px_-10px_rgba(79,70,229,0.7)]"
              transition={{ type: "spring", stiffness: 440, damping: 36 }}
            />
          )}
          <span
            className={cn(
              "relative z-10 grid h-7 w-7 shrink-0 place-items-center rounded-lg transition-all duration-200",
              isActive
                ? "bg-white/20 text-white"
                : "text-slate-400 group-hover:scale-105 group-hover:text-brand-500 dark:group-hover:text-brand-300"
            )}
          >
            <item.icon size={17} />
          </span>
          <span className="relative z-10 truncate">{item.label}</span>
          {isActive && (
            <motion.span
              layoutId={`${scope}-side-active-dot`}
              className="relative z-10 ml-auto h-1.5 w-1.5 rounded-full bg-white"
              transition={{ type: "spring", stiffness: 440, damping: 36 }}
            />
          )}
        </ActiveNavRow>
      )}
    </NavLink>
  );
}

// Scrolls itself into view the moment it becomes the active item — the nav
// list is its own scrolling container (`overflow-y-auto` below), so landing
// on a route whose entry sits further down the list highlighted it without
// ever bringing it into the visible area. `block: "nearest"` only moves the
// list when the item is actually out of view, so it never fights the user's
// own scroll position for an item that's already visible.
function ActiveNavRow({ isActive, children }) {
  const ref = useRef(null);
  useEffect(() => {
    if (isActive) ref.current?.scrollIntoView({ block: "nearest" });
  }, [isActive]);
  return (
    <span
      ref={ref}
      className={cn(
        "relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
        isActive
          ? "text-white"
          : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5"
      )}
    >
      {children}
    </span>
  );
}

// Reused for both the fixed desktop sidebar and the mobile drawer. `scope` MUST
// differ between the two instances — see NavItem.
export default function SidebarNav({ role, onNavigate, scope = "desktop" }) {
  const nav = navForRole(role);
  const { user } = useAuth();
  const navigate = useNavigate();

  const displayName =
    user?.name || (user?.email ? user.email.split("@")[0] : ROLE_LABEL[role] || "User");
  const initials = displayName.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const roleLabel = ROLE_LABEL[role] || "Tester";
  const companyName = user?.company_name;
  // The setting was already saved (backend never had a bug here) — nothing
  // anywhere ever rendered it, so it looked like saving it did nothing (#104).
  // Falls back to the plain icon on a bad/unreachable URL rather than a
  // broken-image glyph.
  const [logoFailed, setLogoFailed] = useState(false);
  const companyLogoUrl = !logoFailed && user?.company_logo_url;
  // Look it up, don't guess from array position — account-nav[0] is Notifications
  // for a developer and What's New for a tester, so the card labelled "Account
  // settings" opened neither for anyone.
  const settingsPath = SETTINGS_PATH[role] || "/dashboard/settings";

  return (
    <div className="relative flex h-full flex-col">
      {/* soft brand glow behind the header */}
      <div className="pointer-events-none absolute -left-12 -top-12 h-40 w-40 rounded-full bg-brand-500/10 blur-3xl dark:bg-brand-500/15" />

      {/* logo — the role is already named under the user's avatar below */}
      <div className="relative flex h-16 items-center gap-2 px-5">
        <Link to={HOME_BY_ROLE[role] || "/dashboard"} onClick={onNavigate} className="min-w-0 transition-opacity hover:opacity-80">
          <Logo />
        </Link>
        {/* Mobile drawer only (onNavigate is the close callback the layout passes).
            Tapping the scrim was the ONLY way out, which is undiscoverable and
            unreachable from the keyboard. */}
        {onNavigate && (
          <button
            type="button"
            onClick={onNavigate}
            aria-label="Close menu"
            className="ml-auto grid h-9 w-9 shrink-0 place-items-center rounded-xl text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/5 dark:hover:text-white lg:hidden"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* company context */}
      {companyName && (
        <div className="px-3 pb-1 pt-1">
          <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 dark:border-white/10 dark:bg-white/5">
            <span className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 text-white shadow-soft">
              {companyLogoUrl ? (
                <img src={companyLogoUrl} alt="" className="h-full w-full object-cover" onError={() => setLogoFailed(true)} />
              ) : (
                <Building2 size={15} />
              )}
            </span>
            <div className="min-w-0">
              <p className="truncate text-[0.625rem] font-semibold uppercase tracking-wider text-slate-400">Company</p>
              <p className="truncate text-sm font-semibold text-slate-800 dark:text-white">{companyName}</p>
            </div>
          </div>
        </div>
      )}

      <nav className="relative flex-1 space-y-1 overflow-y-auto px-3 pb-4">
        <p className="px-3 pb-1 pt-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">
          {nav.section}
        </p>
        {nav.primary.map((item) => (
          <NavItem key={item.to} item={item} onNavigate={onNavigate} scope={scope} />
        ))}

        {nav.account.length > 0 && (
          <>
            <div className="mx-3 my-3 h-px bg-slate-200/70 dark:bg-white/10" />
            <p className="px-3 pb-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">
              Account
            </p>
            {nav.account.map((item) => (
              <NavItem key={item.to} item={item} onNavigate={onNavigate} scope={scope} />
            ))}
          </>
        )}
      </nav>

      {/* clickable user card → Settings */}
      <div className="relative border-t border-slate-200 p-3 dark:border-white/10">
        <button
          type="button"
          onClick={() => {
            if (onNavigate) onNavigate();
            navigate(settingsPath);
          }}
          className="group flex w-full items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-slate-100 dark:hover:bg-white/5"
          title="Account settings"
        >
          <span className="relative grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-sm font-bold text-white shadow-soft">
            {initials}
            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500 dark:border-ink-900" />
          </span>
          <div className="min-w-0 flex-1 text-left">
            <p className="truncate text-sm font-semibold text-slate-800 dark:text-white">{displayName}</p>
            <p className="truncate text-xs text-slate-400">{roleLabel}</p>
          </div>
          <ChevronRight size={15} className="shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
}
